import {
	expressEventListener,
	expressQuerySelector,
	expressQuerySelectorAll,
	expressRemoveClass,
	show,
} from "../../common/html";
import { postHtmlRequest, postRequest } from "../../utils/fetch";
import { initForm } from "../molecules/form";
import { addParameter, getFullQuerystring, getParameter, updateParameter } from "../../utils/querystring";
import { createPopup, IPopup } from "./popup-new";
import { createButton, IButton } from "../atoms/button";
import { compositeDisposable, IDisposable } from "../../utils/disposable";
import { createPageLog, PageLogStyle } from "../molecules/page-log";
import { htmlDecode, stringToBase64 } from "../../utils/string";
import { getCookie } from "../../utils/cookie";

enum ExternalLoginProvider {
	Google = 0,
	Facebook = 1,
}

interface SignInButton {
	Provider: ExternalLoginProvider;
	ButtonEl: HTMLElement;
	Button: IButton;
}

const signInButtons: SignInButton[] = [];

const getMediaPartnerForLabel = function (mediaStorePartnerType: string): string {
	if (mediaStorePartnerType === "googlephotos") {
		return "Google";
	}

	return mediaStorePartnerType;
};

export interface ICreateLoginRegisterDependencies {
	onSuccess?: () => void;
	pageLogEl?: HTMLElement;
	closeLoginPopup?: () => void;
}

export async function createAuthenticationPopupAsync(
	mode?: string,
	addSuccessQueryToRedirectUrl = false,
): Promise<IPopup> {
	const loginPopup = await createPopup({
		dialogId: "LoginRegisterComponent",
		view: "Authentication",
		data: { Email: getParameter("Email"), Mode: mode || "login" },
		forceRefresh: true,
	});

	if (!loginPopup.popup) return undefined;

	window.loginWatcher && window.loginWatcher.cancel();
	CreateLoginRegister(loginPopup.popup, {
		onSuccess: () => {
			addSuccessQueryToRedirectUrl
				? (window.location.href = addParameter("success", "true", window.location.href))
				: window.location.reload();
		},
		pageLogEl: expressQuerySelector(document, ".technical-top-message-log", false),
		closeLoginPopup: loginPopup.close,
	});

	return loginPopup;
}
export function CreateLoginRegisterComponents(addSuccessQueryToRedirectUrl = false) {
	if (!window.context.authenticationPopupEnabled) return;

	const loginComponents = expressQuerySelectorAll(document, ".technical-authentication-link");

	loginComponents.forEach((x) => {
		const button = expressQuerySelector(x, ".technical-authentication-link-button", false);
		button &&
			button.addEventListener("click", async (e: Event) => {
				e.preventDefault();
				const loginPopup = await createAuthenticationPopupAsync(
					button.getAttribute("data-mode"),
					addSuccessQueryToRedirectUrl,
				);
				await loginPopup.openAsync();
			});

		const registerLink = expressQuerySelector(x, ".technical-authentication-register-link");
		registerLink &&
			registerLink.addEventListener("click", async (e: Event) => {
				e.preventDefault();
				const loginPopup = await createAuthenticationPopupAsync(
					registerLink.getAttribute("data-mode"),
					addSuccessQueryToRedirectUrl,
				);
				await loginPopup.openAsync();
			});
	});
}

export function CreateLoginRegister(containerEl?: HTMLElement, deps?: ICreateLoginRegisterDependencies) {
	const loginRegisterEls = expressQuerySelectorAll<HTMLElement>(
		containerEl ?? document,
		".technical-login-register-container",
	);
	const { onSuccess, pageLogEl, closeLoginPopup } = deps;
	const loginComponents = loginRegisterEls.map((x) => {
		return ExpressLoginComponent(x, {
			toggleElementClicked: (loginMode: boolean) => {
				loginComponents.forEach((x) => x.toggleShow(x.loginMode === loginMode));
			},
			onSuccess: onSuccess,
			pageLogEl,
			closeLoginPopup,
		});
	});
}

export interface LoginComponentDeps {
	toggleElementClicked: (loginMode) => void;
	onSuccess: () => void;
	pageLogEl?: HTMLElement;
	closeLoginPopup?: () => void;
}

interface IExpressLoginRequest {
	Email: string;
	Password: string;
	RememberMe: string;
	RedirectUrl: string;
	CurrentUrl: string;
}

interface IExpressLoginResult {
	redirectUrl: string;
	isNotAuthenticated: boolean;
	errorMsg: string;
	blockingErrorMsg: string;
	blockingErrorLabel: string;
	accountId: string;
	cartId: string;
	isSoftDeleted: boolean;
}

interface ILoginSubmitResult {
	readonly redirectUrl?: string;
	readonly isNotAuthenticated?: boolean;
	readonly errorMsg?: string;
	readonly blockingErrorMsg?: string;
	readonly accountId?: string;
	readonly cartId?: string;
	readonly isSoftDeleted?: boolean;
}

interface IExpressRegisterRequest {
	Email: string;
	Password: string;
	ConfirmedPassword: string;
	PersonalMessage: string;
	NewsLetter: string;
	Conditions: string;
	RedirectUrl: string;
}

interface IExpressRegisterResponse {
	success: boolean;
	accountInOtherDomain: string;
	redirectUrl: string;
	accountId: string;
	cartId: string;
	switchToLogin: boolean;
	message: string;
	email: string;
	accountBlocked: string;
	isSoftDeleted: true;
}

interface ILoginUserAtSmartPhotoResult {
	readonly accountData?: IAccountData;
	readonly loginResult: ILoginResult;
	readonly accountType: string;
	readonly cartView?: string;
	readonly msgView?: string;
	readonly redirectUrl?: string;
	readonly success?: boolean;
}

interface IAccountData {
	readonly DeliveryAddressData: IAddress;
	readonly BillingAddressData: IAddress;
	readonly Email: string;
	readonly FirstName: string;
	readonly HideNewsletter: boolean;
	readonly LastName: string;
	readonly Message?: string;
	readonly SendNewsletter?: boolean;
	readonly ShowProfileForm?: boolean;
	readonly ShowProfileFormExtraText?: boolean;
}

export interface IAddress {
	HouseNumber?: string;
	Id?: string;
	IsDefault?: boolean;
	City?: string;
	RegionCountryISO?: string;
	readonly Country?: string;
	FirstName?: string;
	LastName?: string;
	CompanyName?: string;
	Line1?: string;
	Line2?: string;
	Line3?: string;
	Line4?: string;
	ZipCode?: string;
	VatNumber?: string;
	Street?: string;
	BoxNumber?: string;
	ExtraAddressInformation?: string;
}

export interface ICreateFacebookLoginButtonDependencies {
	readonly loginUserAtSmartPhoto: (
		uid: string,
		accessToken: string,
		expire: number,
		mediaStorePartnerType: string,
		newEmail: string,
		skipLogin: boolean,
		onOrderFlow: boolean,
		async: boolean,
	) => Promise<void>;
}

interface ILoginResult {
	AccountId: string;
	tempAccountId: string;
	readonly PersonId: string;
	readonly cartId: string;
	readonly Email: string;
	readonly redirectUrl: string;
	readonly success: string;
	readonly PopUpCase: string;
}

export function ExpressLoginComponent(containerEl: HTMLElement, deps: LoginComponentDeps) {
	const { toggleElementClicked, onSuccess, closeLoginPopup } = deps;
	const loginMode = containerEl.dataset.mode === "register";
	const pageLog = deps.pageLogEl && createPageLog(deps.pageLogEl);
	const form = expressQuerySelector<HTMLFormElement>(containerEl, ".technical-login-register-form", true);
	const externalLoginSubmitButtons = expressQuerySelectorAll<HTMLButtonElement>(
		containerEl,
		".technical-external-login",
	);
	const toggleEl = expressQuerySelector<HTMLElement>(
		containerEl,
		".o-authentication-form__footer .technical-label a",
		false,
	); // link in label
	const forgetPassword = expressQuerySelector<HTMLElement>(containerEl, ".technical-forgot-password", false);

	const login = async (data): Promise<void> => {
		try {
			// If already logged in redirect to my account
			const emptyGuid = "00000000-0000-0000-0000-000000000000";
			const currentAccountId = getCookie("accid") || emptyGuid;
			const currentAccountType = getCookie("acctype");

			if (currentAccountId !== emptyGuid && currentAccountType === "Full") {
				location.reload();
				return;
			}

			const res = await postRequest<IExpressLoginRequest, IExpressLoginResult>(
				"/ajaxxhr/Login/NormalLogin",
				{
					Email: data["loginemail"],
					Password: stringToBase64(data["loginpassword"]),
					RememberMe: data["rememberme"],
					RedirectUrl: decodeURIComponent(getParameter("ReturnUrl")),
					CurrentUrl: window.location.pathname,
				},
				{ headers: { "Content-type": "application/json; charset=utf-8" } },
			);
			handleLoginResult(res, data);
			return;
		} catch (err) {
			await postHtmlRequest(
				"/apicore/logapi/LogInformation",
				{
					InformationText: `user logged in with express with errors: ${err}`,
					Error: true,
				},
				{ headers: { "Content-type": "application/json; charset=utf-8" } },
			);
			window.loginWatcher && window.loginWatcher.start();
			return;
		}
	};
	const createIsSoftDeletedPopup = (view: string, dialogId: string, data: any) => {
		const undeleteAsync = async (formData: any) => {
			const response = await postRequest<any, ILoginResult | ILoginUserAtSmartPhotoResult>(
				"/ajaxxhr/Login/UndeleteLogin",
				formData,
				{ headers: { "Content-type": "application/json; charset=utf-8" } },
			);
			if ((response && (response as any).PopUpCase) || (response as any).loginResult) {
				handleExternalLoginResult(
					response,
					data.LoginUserAtSmartPhotoModelData.MediaStorePartnerType,
					data.LoginUserAtSmartPhotoModelData.Async,
					undefined,
					undefined,
					undefined,
					data.LoginUserAtSmartPhotoModelData.Uid,
					data.LoginUserAtSmartPhotoModelData.AccessToken,
					data.LoginUserAtSmartPhotoModelData.IdToken,
					data.LoginUserAtSmartPhotoModelData.Scope,
				);
			} else {
				handleLoginResult(response as ILoginSubmitResult as IExpressLoginResult, formData);
			}
		};

		const accountIsDeletedPopup = compositeDisposable(undefined);
		createPopup({
			view: view,
			dialogId: dialogId,
			disposeOnClose: true,
			data: {
				Question: "Label.UserLogin.LoginPage.DeletedAccount.Description",
				Title: "Label.UserLogin.LoginPage.DeletedAccount.Title",
				OkMnemonic: "Label.UserLogin.LoginPage.DeletedAccount.Continue",
				CancelMnemonic: "Label.UserLogin.LoginPage.DeletedAccount.Cancel",
			},
			onBeforeClose: () => {
				accountIsDeletedPopup.dispose();
			},
		}).then((popup) => {
			const dialog = expressQuerySelector<HTMLElement>(document, "#" + dialogId, true);
			const okButton = expressQuerySelector<HTMLElement>(dialog, ".technical-remove-confirmation-yes-button", true);
			const cancelButton = expressQuerySelector<HTMLElement>(dialog, ".technical-remove-confirmation-no-button", true);

			accountIsDeletedPopup.add(
				expressEventListener(okButton, "click", function () {
					popup.close();
					undeleteAsync(data);
				}),
			);
			accountIsDeletedPopup.add(
				expressEventListener(cancelButton, "click", function () {
					popup.close();
				}),
			);

			popup.openAsync();
		});
	};
	const externalLogin = async (containerEl: HTMLButtonElement, provider: ExternalLoginProvider): Promise<void> => {
		switch (provider) {
			case ExternalLoginProvider.Google:
				return await createGoogleLogin(containerEl, {
					handleExternalLoginResult: (
						response: ILoginResult | ILoginUserAtSmartPhotoResult,
						mediaStorePartnerType: string,
						async: boolean,
						idToken = "",
						scope = "",
					) =>
						handleExternalLoginResult(
							response,
							mediaStorePartnerType,
							async,
							undefined,
							undefined,
							undefined,
							undefined,
							undefined,
							idToken,
							scope,
						),
				});
			case ExternalLoginProvider.Facebook:
				return await new Promise(() => {
					createFacebookLoginButton(containerEl, { loginUserAtSmartPhoto: loginUserAtSmartPhoto });
				});
		}
	};
	const loginUserAtSmartPhoto = async (
		uid: string,
		accessToken: string,
		expire: number,
		mediaStorePartnerType: string,
		newEmail: string,
		skipLogin: boolean,
		onOrderFlow: boolean,
		async: boolean,
		scope?: string,
		idToken?: string,
	) => {
		const res = await postRequest<
			{
				userId: string;
				accessToken: string;
				expire: number;
				mediastorePartnerType: string;
				newEmail: string;
				skipLogin: boolean;
				onOrderFlow: boolean;
				async: boolean;
				scope?: string;
				idToken?: string;
			},
			ILoginResult | ILoginUserAtSmartPhotoResult
		>(
			"/ajaxxhr/Login/LoginUserAtSmartPhoto",
			{
				userId: uid,
				accessToken: accessToken,
				expire: expire,
				mediastorePartnerType: mediaStorePartnerType,
				newEmail: newEmail,
				skipLogin: skipLogin,
				onOrderFlow: onOrderFlow,
				async: async,
				scope: scope,
				idToken: idToken,
			},
			{ headers: { "Content-type": "application/json; charset=utf-8" } },
		);
		return handleExternalLoginResult(
			res,
			mediaStorePartnerType,
			async,
			undefined,
			undefined,
			undefined,
			uid,
			accessToken,
			idToken,
		);
	};

	const handleLoginResult = (res: IExpressLoginResult, data: any) => {
		if (res.isNotAuthenticated && !res.errorMsg && !res.blockingErrorMsg) res.errorMsg = "Something went wrong";
		if (res.errorMsg) {
			// show error in page
			formComponent.inputs["loginemail"].setError(res.errorMsg);
			formComponent.inputs["loginpassword"].setError(res.errorMsg);
		}

		if (res.isSoftDeleted) {
			const formData = {
				Email: data["loginemail"],
				Password: stringToBase64(data["loginpassword"]),
				RememberMe: data["rememberme"],
				RedirectUrl: decodeURIComponent(getParameter("ReturnUrl")),
			};

			// show popup
			createIsSoftDeletedPopup("ConfirmDialog", "isSoftDeleted", formData);
		}

		if (res.blockingErrorLabel) {
			if (formComponent) {
				formComponent.inputs["loginpassword"].clear();
				formComponent.inputs["loginpassword"].setValue("");
			}

			let bodyLabel = res.blockingErrorLabel;
			let replacements: string[] = [];
			if (bodyLabel.indexOf("|") > 0) {
				const bodyArr = bodyLabel.split("|");
				bodyLabel = bodyArr[0];
				replacements = bodyArr[1].split(",");
			}

			// show popup
			createPopup({
				view: "AccountBlocked",
				dialogId: "blockingErrorMsg",
				data: {
					TitleLabel: "Label.UserLogin.AccountTemporaryBlocked.Title",
					BodyLabel: bodyLabel,
					BodyReplacements: replacements,
					OkButtonLabel: "Label.CloseButtonText",
					ExtraPopupCss: `p-default`,
				},
			}).then((res) => {
				const dialog = expressQuerySelector<HTMLElement>(document, "#blockingErrorMsg", true);
				const okButton = expressQuerySelector<HTMLElement>(dialog, ".technical-general-popup-ok-button", true);

				okButton.addEventListener("click", function () {
					res.close();
				});

				const link = expressQuerySelector(res.popup, "a", false);
				link &&
					link.addEventListener("click", (e) => {
						e.preventDefault();
						res.close();
						openForgotPasswordPopup();
					});

				res.openAsync();
			});
		}

		if (!res.isNotAuthenticated && res.redirectUrl && res.redirectUrl.length > 0) {
			const dataLayer = window.dataLayer || [];
			dataLayer.push({ event: "login" });
			const iframe = expressQuerySelector<HTMLIFrameElement>(document, "#dataLayerIframe", false);
			if (iframe && iframe.contentWindow && (iframe.contentWindow as any).pushDataLayer) {
				(iframe.contentWindow as any).pushDataLayer({ event: "login" });
			}

			if (!getParameter("embed")) {
				// redirect
				return onSuccess ? onSuccess() : (window.location.href = res.redirectUrl);
			} else if (typeof parent != undefined) {
				// @ts-ignore
				parent.eXpress &&
					parent.eXpress.loggedOn &&
					parent.eXpress.loggedOn({
						accountId: res.accountId,
						cartId: res.cartId,
					});
				parent.document.dispatchEvent(new CustomEvent("loggedOn.loginDialog"));
			}

			window.loginWatcher && window.loginWatcher.start();
		}
	};
	const handleExternalLoginResult = (
		response: ILoginResult | ILoginUserAtSmartPhotoResult,
		mediaStorePartnerType: string,
		async: boolean,
		accountId = "",
		tempAccountId = "",
		loginNotCorrect = false,
		uid = "",
		accessToken = "",
		idToken = "",
		scope = "",
	) => {
		window.loginWatcher && window.loginWatcher.cancel();

		let loginUserAtSmartPhotoResponse: ILoginUserAtSmartPhotoResult | undefined = undefined;
		let loginResult: ILoginResult | undefined = undefined;

		if (response && (response as any).loginResult)
			loginUserAtSmartPhotoResponse = response as ILoginUserAtSmartPhotoResult;
		else loginResult = response as ILoginResult;

		let asyncSuccess = true;
		if (async) {
			if (loginResult && loginResult.success == "true") {
				asyncSuccess = false;
			}
		}
		if (async && asyncSuccess && loginUserAtSmartPhotoResponse) {
			loginResult = loginUserAtSmartPhotoResponse.loginResult as ILoginResult;
		}
		if (asyncSuccess && loginResult && loginResult.success == "true") {
			if (loginResult.redirectUrl) {
				if (!getParameter("embed")) {
					onSuccess
						? onSuccess()
						: (window.location.href = loginResult.redirectUrl
								? window.location.origin + loginResult.redirectUrl
								: window.location.origin);
				} else if (typeof parent != undefined) {
					// @ts-ignore
					parent.eXpress &&
						parent.eXpress.loggedOn &&
						parent.eXpress.loggedOn({
							accountId: loginResult.AccountId,
							cartId: loginResult.cartId,
						});
					parent.document.dispatchEvent(new CustomEvent("loggedOn.loginDialog"));
				}
			}
		} else if (loginResult) {
			// We need to correct the result for TEMP accounts (only Popupcase and success are returned)
			if (!loginResult.AccountId && accountId) loginResult.AccountId = accountId;
			if (!loginResult.tempAccountId && tempAccountId) loginResult.tempAccountId = tempAccountId;
			handleLoginFailed(
				loginResult,
				mediaStorePartnerType,
				loginNotCorrect,
				async,
				getMediaPartnerForLabel(mediaStorePartnerType),
				uid,
				accessToken,
				idToken,
				scope,
			);
		}

		window.loginWatcher && window.loginWatcher.start();
	};
	const handleLoginFailed = (
		loginResult: ILoginResult,
		mediaStorePartnerType: string,
		loginNotCorrect: boolean,
		async: boolean,
		mediaStorePartnerLabel: string,
		uid = "",
		accessToken = "",
		idToken = "",
		scope = "",
		expire = 0,
		skipLogin = false,
	) => {
		const alreadyTriedNewEmail = false;
		let accountInOtherDomainCalled = 0;

		switch (loginResult.PopUpCase) {
			case "AccountDeleted":
				createIsSoftDeletedPopup("ConfirmDialog", "isSoftDeleted", {
					Email: loginResult.Email,
					Password: "",
					RememberMe: false,
					RedirectUrl: decodeURIComponent(getParameter("ReturnUrl")),
					LoginUserAtSmartPhotoModelData: {
						UserId: uid,
						AccessToken: accessToken,
						IdToken: idToken,
						Scope: scope,
						Expire: expire,
						MediaStorePartnerType: mediaStorePartnerType,
						NewEmail: "",
						SkipLogin: skipLogin,
						OnOrderFlow: false,
						Async: async,
					},
				});
				break;
			case "AccountBlocked":
				createPopup({
					dialogId: "AccountBlocked",
					view: "GenericPopup",
					data: {
						TitleLabel: "Label.UserLogin.AccountTemporaryBlocked.Title",
						BodyLabel: "Label.UserLogin.AccountBlocked",
						OkButtonLabel: "Label.OkButtonText",
						ExtraPopupCss: `p-default`,
					},
				}).then((res) => {
					const okButton = expressQuerySelector<HTMLElement>(res.popup, ".technical-general-popup-ok-button", true);
					okButton.addEventListener("click", function () {
						res.close();
					});
					res.openAsync();
				});
				break;
			case "AskPassword":
				createPopup({
					dialogId: "AskPassword",
					view: "AskPasswordDialog",
					data: {
						LoginNotCorrect: loginNotCorrect,
						AccountId: loginResult.AccountId,
						AlreadyTriedEmail: alreadyTriedNewEmail,
						MediaStorePartnerLabel: mediaStorePartnerLabel,
					},
					forceRefresh: true,
				}).then((currentPopup) => {
					const formEl = expressQuerySelector<HTMLFormElement>(
						currentPopup.popup,
						".technical-ask-password-form",
						true,
					);
					const sendPassWordLinkEl = expressQuerySelector<HTMLAnchorElement>(
						currentPopup.popup,
						".technical-send-password-link",
						true,
					);

					const confirmAskPassword = async (password: string) => {
						const singleSignOnResponse = await postRequest<
							{
								accountId: string;
								tempAccountId: string;
								mediastorePartnerType: string;
								password: string;
								async: boolean;
							},
							ILoginResult | ILoginUserAtSmartPhotoResult
						>(
							"/ajaxxhr/Login/SingleSignOnConfirmPassword",
							{
								accountId: loginResult.AccountId,
								tempAccountId: loginResult.tempAccountId,
								mediastorePartnerType: mediaStorePartnerType,
								password: stringToBase64(password),
								async: async,
							},
							{ headers: { "Content-type": "application/json; charset=utf-8" } },
						);
						currentPopup.close();
						handleExternalLoginResult(
							singleSignOnResponse,
							mediaStorePartnerType,
							async,
							loginResult.AccountId,
							loginResult.tempAccountId,
							true,
						);
					};

					const sendPassword = (email: string) => {
						return postRequest(
							"/ajaxxhr/Login/SingleSignOnSendPassword",
							{
								email: email,
								url: encodeURIComponent(window.location.href),
							},
							{ headers: { "Content-type": "application/json; charset=utf-8" } },
						).then((passwordRes: any) => {
							if (!passwordRes || !pageLog || !passwordRes.responseLabel) return;

							const logStyle = passwordRes.isSuccess ? PageLogStyle.Success : PageLogStyle.Error;
							pageLog.updateMessageAndStyle(logStyle, passwordRes.responseLabel, true);
							currentPopup.close();
						});
					};

					initForm(formEl, {
						onSubmitForm: (formData) => confirmAskPassword(formData["password"]),
						submitOnEnter: true,
					});

					sendPassWordLinkEl.addEventListener("click", (e) => {
						e.preventDefault();

						const email = sendPassWordLinkEl.dataset.email;
						email && sendPassword(email);
					});

					currentPopup.openAsync();
				});
				break;
			case "AlreadyLoggedIn":
				createPopup({
					dialogId: "AlreadyLoggedIn",
					view: "GenericPopup",
					data: {
						BodyLabel: "Label.UserLogin.LoginPage.AlreadyLoggedIn",
						OkButtonLabel: "Label.OkButtonText",
						ExtraPopupCss: `p-default`,
					},
				}).then((res) => {
					const okButton = expressQuerySelector<HTMLElement>(res.popup, ".technical-general-popup-ok-button", true);
					okButton.addEventListener("click", function () {
						res.close();
					});
					res.openAsync();
				});
				break;
			case "FatalErrorMessage":
				createPopup({
					dialogId: `${mediaStorePartnerLabel}_FatalErrorMessage`,
					view: "GenericPopup",
					data: {
						BodyLabel: "Label.SingleSignOn.FatalError",
						OkButtonLabel: "Label.OkButtonText",
						ExtraPopupCss: `p-default`,
						BodyReplacements: [mediaStorePartnerLabel],
					},
				}).then((res) => {
					const okButton = expressQuerySelector<HTMLElement>(res.popup, ".technical-general-popup-ok-button", true);
					okButton.addEventListener("click", function () {
						res.close();
					});
					res.openAsync();
				});
				break;
			// case "ConfirmEmail": break; // WEB-3835 => no longer used
			// case "OtherEmail": break;// WEB-3835 => no longer used
			case "FbIdAlreadyConnected":
				createPopup({
					dialogId: `${mediaStorePartnerLabel}_AlreadyConnected`,
					view: "GenericPopup",
					data: {
						BodyLabel: `Label.Profile.SingleSignOn.${mediaStorePartnerLabel}.Error.AlreadyConnected`,
						OkButtonLabel: "Label.OkButtonText",
						ExtraPopupCss: `p-default`,
						BodyReplacements: [mediaStorePartnerLabel],
					},
				}).then((res) => {
					const okButton = expressQuerySelector<HTMLElement>(res.popup, ".technical-general-popup-ok-button", true);
					okButton.addEventListener("click", function () {
						res.close();
					});
					res.openAsync();
				});
				break;
			case "AccountInOtherDomain":
				createPopup({
					dialogId: "AccountInOtherDomain",
					view: "AccountInOtherDomainDialog",
					forceRefresh: true,
					data: {
						accountId: loginResult.AccountId,
						alreadyTriedEmail: accountInOtherDomainCalled > 0,
					},
				}).then((res) => {
					initForm(expressQuerySelector(res.popup, ".technical-account-in-other-domain-form"), {
						onSubmitForm: (formData) => {
							res.close();
							return loginUserAtSmartPhoto(
								uid,
								accessToken,
								expire,
								mediaStorePartnerType,
								formData["email"],
								skipLogin,
								false,
								async,
								scope,
								idToken,
							);
						},
						submitOnEnter: true,
					});

					res.openAsync();
				});
				accountInOtherDomainCalled++;
				break;
			case "ShowNewsletter":
				createPopup({
					dialogId: "ShowNewsletter",
					view: "ShowNewsletterDialog",
					data: {},
				}).then((res) => {
					const redirectUrl = loginResult.redirectUrl;
					const confirmShowNewsletter = async (isChecked: boolean): Promise<void> => {
						await postRequest(
							"/ajaxxhr/Login/UpdateNewsletterCheckBox",
							{
								personId: loginResult.PersonId,
								isChecked: isChecked,
							},
							{ headers: { "Content-type": "application/json; charset=utf-8" } },
						);
						res.close();
						redirectUrl &&
							window.setTimeout(function () {
								window.location.href = redirectUrl.replace(/'/g, "");
							}, 0);
					};

					initForm(expressQuerySelector(res.popup, ".technical-show-newsletter-form"), {
						onSubmitForm: (formData) => confirmShowNewsletter(formData["newslettersso"] === "true"),
						submitOnEnter: true,
					});
					res.openAsync();
				});
				break;
			case "ConfirmEmail":
				createPopup({
					dialogId: "confirmation-email",
					view: "GenericPopup",
					forceRefresh: true,
					data: {
						TitleLabel: "Label.SingleSignOn.ConfirmationEmailTitle",
						BodyLabel: "Label.SingleSignOn.ConfirmationEmailQuestion",
						OkButtonLabel: "Label.ContinueButtonText",
						ExtraPopupCss: "p-default",
						ExtraHeaderCss: "m-popup__header__title a-typography__no-decoration",
						BodyReplacements: [loginResult.Email, mediaStorePartnerLabel],
					},
				}).then((res) => {
					const continueButton = expressQuerySelector(res.popup, ".technical-general-popup-ok-button", true);
					const singleSignOnConfirmationEmail = async (dialog: IPopup) => {
						try {
							await postHtmlRequest(
								"/ajaxxhr/Login/SingleSignOnConfirmEmailWs",
								{
									email: loginResult.Email,
									tempAccountId: loginResult.tempAccountId,
									mediastorePartnerType: mediaStorePartnerType,
									returnUrl: "default",
								},
								{ headers: { "Content-type": "application/json; charset=utf-8" } },
							);
							if (dialog) dialog.close();
						} catch (__1) {
							if (dialog) dialog.close();
						}
					};
					continueButton.addEventListener("click", (_) => singleSignOnConfirmationEmail(res));
					res.openAsync();
				});
				break;
			default:
				createPopup({
					dialogId: "generalError",
					view: "GenericPopup",
					data: {
						BodyLabel: "Label.Profile.SingleSignOn." + getMediaPartnerForLabel(mediaStorePartnerType) + ".GeneralError",
						OkButtonLabel: "Label.OkButtonText",
						ExtraPopupCss: `p-default`,
						BodyReplacements: [mediaStorePartnerLabel],
					},
				}).then((res) => {
					const okButton = expressQuerySelector<HTMLElement>(res.popup, ".technical-general-popup-ok-button", true);
					okButton.addEventListener("click", function () {
						res.close();
					});
					res.openAsync();
				});
				break;
		}
	};
	const register = async (data): Promise<void> => {
		try {
			const res = await postRequest<IExpressRegisterRequest, IExpressRegisterResponse>(
				"/ajaxxhr/Login/Registration",
				{
					Email: data["registeremail"],
					Password: stringToBase64(data["registerpassword"]),
					ConfirmedPassword: stringToBase64(data["registerpassword"]),
					PersonalMessage: data["personalmessage"] || "false",
					NewsLetter: data["newsletter"],
					Conditions: data["termsandconditions"],
					RedirectUrl: decodeURIComponent(getParameter("ReturnUrl") || ""),
				},
				{ headers: { "Content-type": "application/json; charset=utf-8" } },
			);
			if (res.switchToLogin && res.success) {
				toggleElementClicked(loginMode);
			}

			if (res.success) {
				window.loginWatcher && window.loginWatcher.stop();
				const datalayer = window.dataLayer || [];
				datalayer.push({ event: "fullAccount" });
				const iframe = expressQuerySelector<HTMLIFrameElement>(document, "#dataLayerIframe", false);
				if (iframe && iframe.contentWindow && (iframe.contentWindow as any).pushDataLayer)
					(iframe.contentWindow as any).pushDataLayer({ event: "fullAccount" });
				if (!getParameter("embed")) {
					// redirect
					onSuccess ? onSuccess() : (window.location.href = res.redirectUrl ? res.redirectUrl : window.location.origin);
				} else if (typeof parent != undefined) {
					// @ts-ignore
					parent.eXpress &&
						parent.eXpress.loggedOn &&
						parent.eXpress.loggedOn({
							accountId: res.accountId,
							cartId: res.cartId,
						});
					parent.document.dispatchEvent(new CustomEvent("loggedOn.loginDialog"));
				}
			} else {
				if (res.message) {
					// show message in page
					formComponent.inputs["registerpassword"].setError(res.message);
				}

				if (res.isSoftDeleted) {
					const formData = {
						Email: data["registeremail"],
						Password: stringToBase64(data["registerpassword"]),
						RememberMe: false,
						RedirectUrl: decodeURIComponent(getParameter("ReturnUrl")),
					};

					// show popup
					createIsSoftDeletedPopup("ConfirmDialog", "isSoftDeleted", formData);
				}

				if (res.accountInOtherDomain) {
					let bodyLabel = res.accountInOtherDomain;
					let replacements: string[] = [];
					if (bodyLabel.indexOf("|") > 0) {
						const bodyArr = bodyLabel.split("|");
						bodyLabel = bodyArr[0];
						replacements = bodyArr[1].split(",");
					}

					// show popup
					createPopup({
						view: "GenericPopup",
						dialogId: "accountInOtherDomain",
						data: {
							TitleLabel: "Label.UserLogin.AccountInOtherDomain.Title",
							BodyLabel: bodyLabel,
							BodyReplacements: replacements,
							OkButtonLabel: "Label.CloseButtonText",
							ExtraPopupCss: `p-default`,
						},
					}).then((res) => {
						const dialog = expressQuerySelector<HTMLElement>(document, "#accountInOtherDomain", true);
						const okButton = expressQuerySelector<HTMLElement>(dialog, ".technical-general-popup-ok-button", true);

						okButton.addEventListener("click", function () {
							res.close();
						});

						res.openAsync();
					});
				}

				if (res.accountBlocked) {
					let bodyLabel = res.accountBlocked;
					let replacements: string[] = [];
					if (bodyLabel.indexOf("|") > 0) {
						const bodyArr = bodyLabel.split("|");
						bodyLabel = bodyArr[0];
						replacements = bodyArr[1].split(",");
					}

					// show popup
					createPopup({
						view: "AccountBlocked",
						dialogId: "accountBlocked",
						data: {
							TitleLabel: "Label.UserLogin.AccountTemporaryBlocked.Title",
							BodyReplacements: replacements,
							BodyLabel: bodyLabel,
							OkButtonLabel: "Label.CloseButtonText",
							ExtraPopupCss: `p-default`,
						},
					}).then((res) => {
						const dialog = expressQuerySelector<HTMLElement>(document, "#accountBlocked", true);
						const okButton = expressQuerySelector<HTMLElement>(dialog, ".technical-general-popup-ok-button", true);

						okButton.addEventListener("click", function () {
							res.close();
						});

						res.openAsync();
					});
				}
				toggleElementClicked(loginMode);
			}
			return;
		} catch (err) {
			await postHtmlRequest(
				"/apicore/logapi/LogInformation",
				{
					InformationText: `user registerd with express with errors ${err}`,
					Error: true,
				},
				{ headers: { "Content-type": "application/json; charset=utf-8" } },
			);
			window.loginWatcher && window.loginWatcher.start();
			return;
		}
	};

	const formComponent = initForm(form, {
		onSubmitForm: !loginMode ? login : register,
		alternativeButton: expressQuerySelector<HTMLButtonElement>(containerEl, ".technical-submit-button"),
	});

	externalLoginSubmitButtons.forEach((x) => externalLogin(x, ExternalLoginProvider[x.dataset.provider]));
	toggleEl &&
		toggleEl.addEventListener("click", (e) => {
			e.preventDefault();
			toggleElementClicked(loginMode);
		});
	const openForgotPasswordPopup = (successMsg?: string) => {
		createPopup({
			dialogId: "forgotPassword",
			view: "ForgotPasswordDialog",
			forceRefresh: true,
			closeOnClickOutsidePopup: true,
		}).then((res) => {
			const formEl = expressQuerySelector<HTMLFormElement>(res.popup, ".technical-forgot-password-form");
			initForm(formEl, {
				onSubmitForm: (data: { [inputName: string]: string }) =>
					postRequest(
						"/apicore/authenticationapi/forgotpassword",
						{ email: data["email"] },
						{ headers: { "Content-type": "application/json; charset=utf-8" } },
					).then((_) => {
						if (successMsg && deps.pageLogEl) {
							successMsg = successMsg.replace("[Email]", data["email"]);
							pageLog && pageLog.updateMessageAndStyle(PageLogStyle.Success, successMsg, true);
							if (closeLoginPopup) closeLoginPopup();
						}

						res.close();
					}),
				submitOnEnter: true,
			});
			res.openAsync();
		});
	};
	forgetPassword &&
		forgetPassword.addEventListener("click", (e) => {
			e.preventDefault();
			const successMsg = htmlDecode(forgetPassword.dataset.successmsg) || "";
			openForgotPasswordPopup(successMsg);
		});

	return {
		loginMode,
		toggleShow: (force?: boolean) => {
			containerEl.classList.toggle("u-hide", force);

			if (force) return;

			getFullQuerystring().includes("embed") &&
				containerEl.scrollIntoView({
					inline: "start",
					block: "start",
					behavior: "auto",
				});
			history.replaceState(null, "", updateParameter("mode", containerEl.dataset.mode).toLowerCase());
		},
	};
}

const enableButton = (button: IButton) => {
	button.enable();

	const item = signInButtons.filter((b) => b.Button == button)[0];
	item && expressRemoveClass(item.ButtonEl, "skeleton-loading");
};

export async function createGoogleLogin(
	googleLoginButtonEl: HTMLButtonElement,
	deps: {
		handleExternalLoginResult: (
			response: ILoginResult | ILoginUserAtSmartPhotoResult,
			mediaStorePartnerType: string,
			async: boolean,
			idToken: string,
			scope?: string,
		) => void;
	},
): Promise<void> {
	const { handleExternalLoginResult } = deps;

	const googleButton = createButton(googleLoginButtonEl);
	signInButtons.push({ Provider: ExternalLoginProvider.Google, ButtonEl: googleLoginButtonEl, Button: googleButton });

	const initClient = () => {
		if (typeof google === "undefined") {
			setTimeout(initClient, 50);
			return;
		}

		const clientId = googleLoginButtonEl.dataset.appid || "";
		const scope = googleLoginButtonEl.dataset.scope;

		// authenticate - login
		google.accounts.id.initialize({
			client_id: clientId,
			use_fedcm_for_button: true, // use_fedcm_for_button is missing in the definition-file but is required for fedcm to work
			callback: async (response: google.accounts.id.CredentialResponse) => {
				window.loginWatcher && window.loginWatcher.cancel();

				const res = await postRequest<
					{
						jwtToken: string;
						onOrderFlow: boolean;
						scope?: string;
					},
					ILoginResult | ILoginUserAtSmartPhotoResult
				>(
					"/ajaxxhr/Login/LoginWithGoogle",
					{
						jwtToken: response.credential,
						onOrderFlow: false,
						scope: scope,
					},
					{ headers: { "Content-type": "application/json; charset=utf-8" } },
				);
				handleExternalLoginResult(res, "googlephotos", false, response.credential, scope);

				window.loginWatcher && window.loginWatcher.start();
			},
		});
		google.accounts.id.renderButton(googleLoginButtonEl, {
			type: "standard",
			shape: "rectangular",
			size: "large",
			width: 250,
			locale: window.context.locale,
		});

		enableButton(googleButton);
	};

	// Add the script only once
	const googleScriptSrc = "https://accounts.google.com/gsi/client?hl=" + window.context.language;
	const scripts = document.getElementsByTagName("script");
	for (const item of scripts) {
		if (item.src === googleScriptSrc) {
			// Script-tag already exists. Manually call init-func
			initClient();
			return;
		}
	}

	// load google script async
	const scriptEl = document.createElement("script");
	scriptEl.type = "text/javascript";
	scriptEl.async = true;
	scriptEl.defer = true;
	scriptEl.onload = initClient;
	scriptEl.src = googleScriptSrc;
	const firstScriptEl = document.getElementsByTagName("script")[0];
	firstScriptEl.parentElement!.insertBefore(scriptEl, firstScriptEl);
}

export function createFacebookLoginButton(
	facebookLoginButtonEl: HTMLButtonElement,
	deps: ICreateFacebookLoginButtonDependencies,
) {
	const { loginUserAtSmartPhoto } = deps;
	let disposableEvent: IDisposable;
	let isFacebookInitialized = false;
	let onOrderFlow = false;

	const mediaStorePartnerType = "facebook";

	const facebookButton = createButton(facebookLoginButtonEl);
	signInButtons.push({
		Provider: ExternalLoginProvider.Facebook,
		ButtonEl: facebookLoginButtonEl,
		Button: facebookButton,
	});

	const onClick = (ev: Event) => {
		if (ev.preventDefault) {
			ev.preventDefault();
		}
		ev.stopPropagation();
		facebookButton.disable();
		const scopes = facebookLoginButtonEl.dataset.scope;
		loginToFacebook(scopes, false, true);
	};

	const init = () => {
		if (typeof FB === "undefined") return;

		if (!isFacebookInitialized) {
			const appId = facebookLoginButtonEl.dataset.appid ? facebookLoginButtonEl.dataset.appid : "";
			initFacebook(appId);
		}

		disposableEvent = expressEventListener(facebookLoginButtonEl, "click", onClick);
		enableButton(facebookButton);
	};

	const initFacebook = (appId: string) => {
		FB.init({ appId: appId, status: false, cookie: true, version: "v23.0" });
		isFacebookInitialized = true;
	};

	const loginToFacebook = (scopes: string, skipLogin: boolean, async: boolean) => {
		if (typeof FB === "undefined") return;

		if (skipLogin === null) skipLogin = false;
		onOrderFlow = async;

		FB.login(
			function (loginResponse) {
				if (
					!loginResponse ||
					loginResponse.status !== "connected" ||
					!loginResponse.authResponse ||
					!loginResponse.authResponse.userID
				)
					return enableButton(facebookButton);

				const uid = loginResponse.authResponse.userID;

				// check if all permissions are granted (result is also 'connected' when permissions are partialy granted)
				FB.api("/" + uid + "/permissions", (permissionResponse) => {
					if (!permissionResponse || !permissionResponse.data || permissionResponse.data.length == 0)
						return enableButton(facebookButton);

					if (!checkFacebookPermissions(scopes, permissionResponse.data)) return enableButton(facebookButton);

					// user granted all permissions
					const accessToken = loginResponse.authResponse.accessToken;
					const expireTime = loginResponse.authResponse.expiresIn;
					loginUserAtSmartPhoto(
						uid,
						accessToken,
						expireTime,
						mediaStorePartnerType,
						"",
						skipLogin,
						onOrderFlow,
						async,
					).finally(() => enableButton(facebookButton));
				});
			},
			{ scope: scopes, auth_type: "rerequest" },
		);
	};

	const checkFacebookPermissions = (requestedScopes: string, data): boolean => {
		if (!data) return false;
		if (!requestedScopes) return false;

		const requestedScopesArr: string[] = requestedScopes.split(" ");
		const grantedScopes: string[] = data.filter((e) => e.status === "granted").map((e) => e.permission);
		const allowedScopes = grantedScopes.filter((grantedScope: string) => requestedScopesArr.includes(grantedScope));

		return allowedScopes.length == requestedScopesArr.length;
	};

	const deInit = () => disposableEvent && disposableEvent.dispose();

	init();

	return {
		dispose: deInit,
	};
}
